import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { CheckCircle2, ChevronRight, ChevronLeft, Store, FileText, UserCircle, CreditCard, UtensilsCrossed, MapPin, Clock, Upload, AlertCircle, Check, X } from 'lucide-react';

const STEPS = [
  { num: 1, label: 'Business Info', icon: Store, description: 'Basic business details' },
  { num: 2, label: 'Contact & Legal', icon: UserCircle, description: 'Contact info and legal details' },
  { num: 3, label: 'Menu Upload', icon: UtensilsCrossed, description: 'Upload your menu' },
  { num: 4, label: 'Operating Hours', icon: Clock, description: 'Set your business hours' },
  { num: 5, label: 'Banking Info', icon: CreditCard, description: 'Payment setup' },
  { num: 6, label: 'Documents', icon: Upload, description: 'Upload required documents' },
  { num: 7, label: 'Review & Submit', icon: FileText, description: 'Review and submit' },
];

const BUSINESS_TYPES = [
  'Restaurant',
  'Fast Food',
  'Cafe',
  'Bakery',
  'Food Truck',
  'Catering',
  'Grocery Store',
  'Convenience Store',
  'Other'
];

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function VendorOnboarding() {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [stepData, setStepData] = useState({});
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/vendor/onboarding-status');
        const step = res.data.current_step || res.data.onboarding_step || 1;
        const status = (res.data.onboarding_status || '').toLowerCase();
        
        setCurrentStep(step);
        
        // If already completed, redirect to dashboard
        if (status === 'completed' && step >= 7) {
          navigate('/vendor');
        }
      } catch (err) { 
        console.error(err); 
      } finally { 
        setLoading(false); 
      }
    };
    fetch();
  }, [navigate]);

  const handleSaveAndContinueLater = async () => {
    setSubmitting(true);
    try {
      // Save current step data without incrementing step
      await api.post(`/vendor/onboarding/step/${currentStep}`, { 
        data: { ...stepData, _saveMode: 'save' } 
      });
      toast.success('Progress saved! You can continue anytime.');
      
      // Update user context to reflect in_progress status
      const updatedUser = { ...user, onboarding_status: 'in_progress' };
      setUser(updatedUser);
      localStorage.setItem('r2yn_user', JSON.stringify(updatedUser));
      
      // Redirect to vendor dashboard
      navigate('/vendor');
    } catch (err) { 
      toast.error('Failed to save progress');
    } finally { 
      setSubmitting(false); 
    }
  };

  const handleFileUpload = async (file, documentType) => {
    if (!file) return;
    
    // Validate file
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    
    if (file.size > maxSize) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF, JPG, and PNG files are allowed');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post(
        `/vendor/onboarding/documents/upload?document_type=${documentType}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success(`${documentType} uploaded successfully!`);
      
      // Add to uploaded docs list
      setUploadedDocs(prev => [...prev, res.data.document]);
      
      // Fetch updated list
      fetchUploadedDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };
  
  const fetchUploadedDocuments = async () => {
    try {
      const res = await api.get('/vendor/onboarding/documents');
      setUploadedDocs(res.data.documents || []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    }
  };
  
  const handleDeleteDocument = async (docId) => {
    try {
      await api.delete(`/vendor/onboarding/documents/${docId}`);
      toast.success('Document deleted');
      setUploadedDocs(prev => prev.filter(d => d.id !== docId));
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };
  
  // Fetch documents when reaching step 6
  useEffect(() => {
    if (currentStep === 6) {
      fetchUploadedDocuments();
    }
  }, [currentStep]);

  const submitStep = async () => {
    // Validation
    if (!validateStep()) return;
    
    setSubmitting(true);
    try {
      const res = await api.post(`/vendor/onboarding/step/${currentStep}`, { data: stepData });
      toast.success(`Step ${currentStep} completed!`);
      
      if (currentStep === 7) {
        // Final step - mark as completed
        const updatedUser = { ...user, onboarding_step: 7, onboarding_status: 'completed' };
        setUser(updatedUser);
        localStorage.setItem('r2yn_user', JSON.stringify(updatedUser));
        toast.success('Onboarding complete! Your profile is under review.');
        navigate('/vendor');
      } else {
        setCurrentStep(currentStep + 1);
        setStepData({});
      }
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to submit'); 
    } finally { 
      setSubmitting(false); 
    }
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!stepData.name) {
          toast.error('Business name is required');
          return false;
        }
        if (!stepData.business_type) {
          toast.error('Business type is required');
          return false;
        }
        if (!stepData.address) {
          toast.error('Business address is required');
          return false;
        }
        break;
      case 2:
        if (!stepData.phone) {
          toast.error('Phone number is required');
          return false;
        }
        if (!stepData.tax_id) {
          toast.error('Tax ID is required');
          return false;
        }
        break;
      case 3:
        if (!stepData.cuisine_type && !stepData.skip_menu) {
          toast.error('Please specify your cuisine type or skip for later');
          return false;
        }
        break;
      case 5:
        if (!stepData.bank_name) {
          toast.error('Bank name is required');
          return false;
        }
        if (!stepData.account_number) {
          toast.error('Account number is required');
          return false;
        }
        break;
      case 7:
        if (!stepData.signature) {
          toast.error('Please sign to complete onboarding');
          return false;
        }
        if (!stepData.agreed) {
          toast.error('You must agree to the terms');
          return false;
        }
        break;
    }
    return true;
  };

  if (loading) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;

  const progress = ((currentStep - 1) / 6) * 100;

  const renderStepForm = () => {
    switch (currentStep) {
      case 1: // Business Information
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Business Name *</Label>
              <Input 
                value={stepData.name || ''} 
                onChange={e => setStepData({...stepData, name: e.target.value})} 
                placeholder="Mario's Pizza" 
                data-testid="onboarding-business-name" 
              />
            </div>
            
            <div className="space-y-2">
              <Label>Business Type *</Label>
              <Select 
                value={stepData.business_type || ''} 
                onValueChange={value => setStepData({...stepData, business_type: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Business Description</Label>
              <Textarea 
                value={stepData.description || ''} 
                onChange={e => setStepData({...stepData, description: e.target.value})} 
                placeholder="Tell us about your business..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Street Address *</Label>
                <Input 
                  value={stepData.address || ''} 
                  onChange={e => setStepData({...stepData, address: e.target.value})} 
                  placeholder="123 Main St" 
                />
              </div>
              <div className="space-y-2">
                <Label>City *</Label>
                <Input 
                  value={stepData.city || ''} 
                  onChange={e => setStepData({...stepData, city: e.target.value})} 
                  placeholder="New York" 
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>State</Label>
                <Input 
                  value={stepData.state || ''} 
                  onChange={e => setStepData({...stepData, state: e.target.value})} 
                  placeholder="NY" 
                  maxLength={2}
                />
              </div>
              <div className="space-y-2">
                <Label>ZIP Code</Label>
                <Input 
                  value={stepData.zip || ''} 
                  onChange={e => setStepData({...stepData, zip: e.target.value})} 
                  placeholder="10001" 
                />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Input 
                  value={stepData.country || 'USA'} 
                  onChange={e => setStepData({...stepData, country: e.target.value})} 
                  placeholder="USA" 
                />
              </div>
            </div>
          </div>
        );

      case 2: // Contact & Legal
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Business Phone *</Label>
              <Input 
                type="tel"
                value={stepData.phone || ''} 
                onChange={e => setStepData({...stepData, phone: e.target.value})} 
                placeholder="+1-555-0200" 
              />
            </div>

            <div className="space-y-2">
              <Label>Business Email</Label>
              <Input 
                type="email"
                value={stepData.business_email || ''} 
                onChange={e => setStepData({...stepData, business_email: e.target.value})} 
                placeholder="contact@mariospizza.com" 
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tax ID / EIN *</Label>
                <Input 
                  value={stepData.tax_id || ''} 
                  onChange={e => setStepData({...stepData, tax_id: e.target.value})} 
                  placeholder="12-3456789" 
                />
              </div>
              <div className="space-y-2">
                <Label>Business License Number</Label>
                <Input 
                  value={stepData.license_number || ''} 
                  onChange={e => setStepData({...stepData, license_number: e.target.value})} 
                  placeholder="LIC-12345" 
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Owner/Manager Name</Label>
              <Input 
                value={stepData.owner_name || ''} 
                onChange={e => setStepData({...stepData, owner_name: e.target.value})} 
                placeholder="Mario Rossi" 
              />
            </div>
          </div>
        );

      case 3: // Menu Upload
        return (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                You can set up your full menu after onboarding. For now, give us basic info.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label>Primary Cuisine Type</Label>
              <Input 
                value={stepData.cuisine_type || ''} 
                onChange={e => setStepData({...stepData, cuisine_type: e.target.value})} 
                placeholder="Italian, Mexican, Chinese, etc." 
              />
            </div>

            <div className="space-y-2">
              <Label>Approximate Number of Menu Items</Label>
              <Input 
                type="number"
                value={stepData.menu_items_count || ''} 
                onChange={e => setStepData({...stepData, menu_items_count: e.target.value})} 
                placeholder="20" 
              />
            </div>

            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="skip-menu"
                checked={stepData.skip_menu || false} 
                onChange={e => setStepData({...stepData, skip_menu: e.target.checked})} 
                className="rounded" 
              />
              <Label htmlFor="skip-menu" className="text-sm">Skip for now, I'll upload my menu later</Label>
            </div>

            <p className="text-xs text-muted-foreground mt-2">
              💡 Tip: You can use CSV import to add all your items at once from the Vendor Dashboard.
            </p>
          </div>
        );

      case 4: // Operating Hours & Delivery Zones
        return (
          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-3">Operating Hours</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Opening Time</Label>
                  <Input 
                    type="time"
                    value={stepData.hours_open || ''} 
                    onChange={e => setStepData({...stepData, hours_open: e.target.value})} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Closing Time</Label>
                  <Input 
                    type="time"
                    value={stepData.hours_close || ''} 
                    onChange={e => setStepData({...stepData, hours_close: e.target.value})} 
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                You can set specific hours for each day later in settings.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Operating Days</Label>
              <div className="grid grid-cols-2 gap-2">
                {DAYS_OF_WEEK.map(day => (
                  <div key={day} className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id={`day-${day}`}
                      checked={(stepData.operating_days || []).includes(day)} 
                      onChange={e => {
                        const days = stepData.operating_days || [];
                        if (e.target.checked) {
                          setStepData({...stepData, operating_days: [...days, day]});
                        } else {
                          setStepData({...stepData, operating_days: days.filter(d => d !== day)});
                        }
                      }}
                      className="rounded" 
                    />
                    <Label htmlFor={`day-${day}`} className="text-sm">{day}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Delivery Radius (miles)</Label>
              <Input 
                type="number"
                value={stepData.delivery_radius || ''} 
                onChange={e => setStepData({...stepData, delivery_radius: e.target.value})} 
                placeholder="10" 
              />
              <p className="text-xs text-muted-foreground">
                How far are you willing to deliver from your location?
              </p>
            </div>
          </div>
        );

      case 5: // Banking/Payout Details
        return (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Your banking information is encrypted and secure. Payouts are processed weekly.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label>Bank Name *</Label>
              <Input 
                value={stepData.bank_name || ''} 
                onChange={e => setStepData({...stepData, bank_name: e.target.value})} 
                placeholder="Chase Bank" 
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Routing Number</Label>
                <Input 
                  value={stepData.routing_number || ''} 
                  onChange={e => setStepData({...stepData, routing_number: e.target.value})} 
                  placeholder="021000021" 
                  maxLength={9}
                />
              </div>
              <div className="space-y-2">
                <Label>Account Number *</Label>
                <Input 
                  type="password"
                  value={stepData.account_number || ''} 
                  onChange={e => setStepData({...stepData, account_number: e.target.value})} 
                  placeholder="••••••••" 
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Account Type</Label>
              <Select 
                value={stepData.account_type || 'checking'} 
                onValueChange={value => setStepData({...stepData, account_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="checking">Checking</SelectItem>
                  <SelectItem value="savings">Savings</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <p className="text-xs text-muted-foreground">
              R2YN uses Stripe for secure payment processing. Commission rate: 15%
            </p>
          </div>
        );

      case 6: // Document Upload
        return (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Upload copies of required documents. All files must be PDF, JPG, or PNG (max 5MB each).
              </AlertDescription>
            </Alert>

            {/* Document Upload Fields */}
            <div className="space-y-4">
              {['Business License', 'Food Handler Certificate', 'Liability Insurance'].map(docType => {
                const uploaded = uploadedDocs.find(d => d.document_type === docType);
                return (
                  <div key={docType} className="border rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">{docType}</Label>
                      {uploaded && (
                        <div className="flex items-center gap-2 text-xs text-emerald-600">
                          <Check className="h-3 w-3" />
                          <span>Uploaded</span>
                        </div>
                      )}
                    </div>
                    
                    {uploaded ? (
                      <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
                        <div className="flex items-center gap-2 text-sm">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span className="truncate max-w-xs">{uploaded.file_name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({(uploaded.size_bytes / 1024).toFixed(0)} KB)
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDocument(uploaded.id)}
                          className="h-7 w-7 p-0"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="relative">
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(file, docType);
                          }}
                          disabled={uploading}
                          className="cursor-pointer"
                        />
                        {uploading && (
                          <div className="absolute inset-0 bg-background/80 flex items-center justify-center rounded">
                            <span className="text-xs text-muted-foreground">Uploading...</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex items-center gap-2 mt-3">
              <input 
                type="checkbox" 
                id="upload-later"
                checked={stepData.upload_later || false} 
                onChange={e => setStepData({...stepData, upload_later: e.target.checked})} 
                className="rounded" 
              />
              <Label htmlFor="upload-later" className="text-sm">I'll upload documents later</Label>
            </div>

            <p className="text-xs text-muted-foreground">
              Note: Your account will remain "Under Review" until all required documents are verified.
            </p>
          </div>
        );

      case 7: // Review & Submit
        return (
          <div className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg">
              <h4 className="font-medium mb-3">Review Your Information</h4>
              <div className="space-y-2 text-sm">
                <p><strong>Business:</strong> {stepData.name || user?.name || 'Not provided'}</p>
                <p><strong>Type:</strong> {stepData.business_type || 'Not provided'}</p>
                <p><strong>Address:</strong> {stepData.address || 'Not provided'}</p>
                <p><strong>Phone:</strong> {stepData.phone || 'Not provided'}</p>
                <p><strong>Cuisine:</strong> {stepData.cuisine_type || 'To be added later'}</p>
              </div>
            </div>

            <Alert>
              <FileText className="h-4 w-4" />
              <AlertDescription>
                <strong>Vendor Agreement Summary:</strong><br />
                By submitting, you agree to R2YN's Vendor Terms including a 15% commission on orders, compliance with food safety regulations, and quality standards.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label>Type your full name to sign *</Label>
              <Input 
                value={stepData.signature || ''} 
                onChange={e => setStepData({...stepData, signature: e.target.value})} 
                placeholder="Your full legal name" 
                data-testid="onboarding-signature" 
              />
            </div>

            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="agree"
                checked={stepData.agreed || false} 
                onChange={e => setStepData({...stepData, agreed: e.target.checked})} 
                className="rounded" 
              />
              <Label htmlFor="agree" className="text-sm">I agree to the R2YN Vendor Terms of Service *</Label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">Vendor Onboarding</h1>
        <p className="text-muted-foreground mt-1">Complete all 7 steps to activate your store on R2YN.</p>
      </div>

      <Progress value={progress} className="h-2" />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Step List */}
        <div className="lg:col-span-1">
          <div className="space-y-1">
            {STEPS.map(step => {
              const Icon = step.icon;
              const isComplete = step.num < currentStep;
              const isCurrent = step.num === currentStep;
              return (
                <div key={step.num} className={`flex items-center gap-3 p-2.5 rounded-lg text-sm transition-colors ${
                  isCurrent ? 'bg-accent/60 text-accent-foreground' :
                  isComplete ? 'text-emerald-600' : 'text-muted-foreground'
                }`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${
                    isComplete ? 'bg-emerald-100 text-emerald-700' :
                    isCurrent ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    {isComplete ? <CheckCircle2 className="h-4 w-4" /> : step.num}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{step.label}</p>
                    <p className="text-xs text-muted-foreground truncate">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Form */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {React.createElement(STEPS[currentStep - 1].icon, { className: 'h-5 w-5' })}
                Step {currentStep}: {STEPS[currentStep - 1].label}
              </CardTitle>
              <CardDescription>{STEPS[currentStep - 1].description}</CardDescription>
            </CardHeader>
            <CardContent>
              {renderStepForm()}
              
              <div className="flex justify-between mt-8 pt-4 border-t">
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    disabled={currentStep <= 1}
                    onClick={() => setCurrentStep(currentStep - 1)}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" /> Back
                  </Button>
                  
                  {currentStep < 7 && (
                    <Button
                      variant="outline"
                      onClick={handleSaveAndContinueLater}
                      disabled={submitting}
                    >
                      Save & Continue Later
                    </Button>
                  )}
                </div>
                
                <Button
                  onClick={submitStep}
                  disabled={submitting}
                  data-testid="vendor-onboarding-next-button"
                >
                  {submitting ? 'Submitting...' : currentStep === 7 ? 'Complete & Submit' : 'Next'}
                  {currentStep < 7 && <ChevronRight className="h-4 w-4 ml-1" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
